// src/store/slices/conversationsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAllConversations, assignConversation, deleteChat, sendMessageToLead } from '../../services/whatsapp';
import moment from 'moment';
import { RootState } from '../store';
import logger from '../../utils/logger';

// Types
export interface User {
  id: string;
  _id: string;
  name: string;
  mobile: string;
  email?: string;
  userIcon: string;
  msg: string;
  timeStamp: string;
  msgStatus: string;
  lastMessageAt: string;
  lead: string;
  label?: Array<string>; 
  status?: Array<string>; 
  assignedTo: Array<any>;
  convoId: string;
  unReadCount?: number;
  // Add the lastMessage property to the User interface
  lastMessage?: {
    _id?: string;
    messageDelivery?: string;
    messageType?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'reaction' | 'template';
    messageId?: string;
    message?: {
      message_id?: string;
      emoji?: string;
      body?: string | any;
      filename?: string;
      mime_type?: string;
      [key: string]: any;
    };
    status?: string;
    sender?: any;
    createdAt?: string;
    [key: string]: any;
  };
}

export interface ConversationGroup {
  title: string; // Date
  data: User[];
}

export interface ConversationsState {
  groups: ConversationGroup[];
  selectedConversation: User | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  totalConversations: number;
  currentPage: number;
  filters: {
    search?: string;
    isRead?: boolean;
    isPinned?: boolean;
    isAssigned?: boolean;
    isAssignedToMe?: boolean;
    isAutomation?: boolean;
  };
}

// Initial state
const initialState: ConversationsState = {
  groups: [],
  selectedConversation: null,
  status: 'idle',
  error: null,
  totalConversations: 0,
  currentPage: 1,
  filters: {}
};

// Helper function
const getDate = (timestamp: string) => timestamp?.split('T')[0];

// Thunks
export const fetchConversations = createAsyncThunk(
  'conversations/fetchConversations',
  async ({ page, refresh = false }: { page: number, refresh?: boolean }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { filters } = state.whatsapp.conversations;
      
      // Base pagination data
      const data = {
        orderBy: "_id",
        isAscending: false,
        page,
        perPage: 15,
      };
      
      // Build params from filters
      const params: any = {
        page,
        perPage: 15,
        isInInbox: filters.isAutomation ? true : false,
      };
      
      // Add any active filters
      if (filters.search) params.search = filters.search;
      if (filters.isRead === false) params.isRead = false;
      if (filters.isPinned === true) params.isPinned = true;
      if (filters.isAssigned === false) params.isAssigned = false;
      if (filters.isAssignedToMe === true) params.isAssignedToMe = true;

      logger.log("Filters object:", filters);
logger.log("API parameters being sent:", params);

      const response = await getAllConversations(data, params);
      
      return {
        data: response.data.data,
        total: response.data.total,
        page,
        refresh
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch conversations');
    }
  }
);

export const assignConversationToUser = createAsyncThunk(
  'conversations/assignConversation',
  async ({ conversationId, userId }: { conversationId: string, userId: string }, { rejectWithValue }) => {
    try {
      const data = {
        assignToIds: [userId],
      };
      
      const response = await assignConversation(conversationId, data);
      return {
        conversationId,
        assignedUser: response.data?.data.assignToUsers?.[0]
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to assign conversation');
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'conversations/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await deleteChat(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete conversation');
    }
  }
);

export const startNewChat = createAsyncThunk(
  'conversations/startNewChat',
  async ({ phone, message = "Hello!" }: { phone: string, message?: string }, { rejectWithValue }) => {
    try {
      const payload = {
        phone,
        type: "text",
        phoneId: "190076364199723", // This should come from user settings
        text: {
          body: message,
        },
      };
      
      await sendMessageToLead(payload);
      
      // After sending message, we need to fetch conversations to get the new chat
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to start new chat');
    }
  }
);

// Create the slice
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setSelectedConversation: (state, action: PayloadAction<User | null>) => {
      state.selectedConversation = action.payload;
    },
    
    clearSelectedConversation: (state) => {
      state.selectedConversation = null;
    },
    
   setFilter: (state, action: PayloadAction<{ key: keyof ConversationsState['filters'], value: any }>) => {
  const { key, value } = action.payload;
  
  // ✅ ENHANCED: Always reset pagination when any filter changes
  state.currentPage = 1;
  
  // ✅ ENHANCED: Clear groups when starting new search
  if (key === 'search') {
    state.groups = [];
    state.totalConversations = 0;
  }
  
  if (value === undefined || value === '' || value === null) {
    // ✅ Remove filter if value is empty/null/undefined
    const { [key]: _, ...remaining } = state.filters;
    state.filters = remaining;
  } else {
    // ✅ Add/update filter
    state.filters = { ...state.filters, [key]: value };
  }
},
    
  toggleFilter: (state, action: PayloadAction<keyof ConversationsState['filters']>) => {
  const key = action.payload;
  
  // ✅ ENHANCED: Reset pagination and clear data for new filter
  state.currentPage = 1;
  state.groups = [];
  state.totalConversations = 0;
  
  // ✅ Check if the filter exists, regardless of its value
  if (key in state.filters) {
    // Remove filter if it exists
    const { [key]: _, ...remaining } = state.filters;
    state.filters = remaining;
  } else {
    // ✅ For most filters, the active value is true, but there are exceptions
    let value;
    
    // Special cases for filter values
    if (key === 'isRead' || key === 'isAssigned') {
      // "Unread" and "Unassigned" filters should set false
      value = false;
    } else {
      // All other filters set true when active
      value = true;
    }
    
    state.filters = { 
      ...state.filters, 
      [key]: value
    };
  }
},
    
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    // ✅ NEW: Add reset pagination action
resetPagination: (state) => {
  state.currentPage = 1;
  state.groups = [];
  state.totalConversations = 0;
},

    updateLastMessage: (state, action: PayloadAction<{ 
      conversationId: string, 
      message: any 
    }>) => {
      const { conversationId, message } = action.payload;
      
      // Update in grouped data
      state.groups = state.groups.map(group => {
        const updatedData = group.data.map(conversation => {
          if (conversation.convoId === conversationId) {
            return {
              ...conversation,
              lastMessageAt: message.createdAt,
              msg: typeof message.message.body === 'object' 
                ? (message.message.body?.text?.body || '') 
                : message.message.body,
              timeStamp: moment(message.createdAt).format('LT')
            };
          }
          return conversation;
        });
        
        return {
          ...group,
          data: updatedData
        };
      });
      
      // Also update in selected conversation if it matches
      if (state.selectedConversation?.convoId === conversationId) {
        state.selectedConversation = {
          ...state.selectedConversation,
          lastMessageAt: message.createdAt,
          msg: typeof message.message.body === 'object' 
            ? (message.message.body?.text?.body || '') 
            : message.message.body,
          timeStamp: moment(message.createdAt).format('LT')
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchConversations
      .addCase(fetchConversations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        
        const { data, total, page, refresh } = action.payload;
        state.totalConversations = total;
        
        // Process the conversation data
        const inboxList = data;
        
        // Create properly typed groups
        const groupedData: { [date: string]: User[] } = {};
        
        // Group conversations by date
        inboxList.forEach((item: any) => {
          const date = getDate(item.lastMessageAt);
          if (!groupedData[date]) {
            groupedData[date] = [];
          }
          
          // Get the appropriate message preview based on message type
          let messagePreview = '';
          const messageType = item?.lastMessage?.messageType || 'text';
          
          // Handle different message types for the preview
          switch(messageType) {
            case 'reaction':
              // For reactions, use the emoji
              const emoji = item?.lastMessage?.message?.emoji;
              messagePreview = emoji ? `Reacted with ${emoji}` : 'Reacted to a message';
              break;
              
            case 'image':
              messagePreview = '📷 Photo';
              break;
              
            case 'video':
              messagePreview = '🎥 Video';
              break;
              
            case 'document':
              // For documents, use the filename if available
              const filename = item?.lastMessage?.message?.filename || 'Document';
              messagePreview = `📄 ${filename}`;
              break;
              
            case 'audio':
              messagePreview = '🔊 Audio';
              break;
              
            case 'template':
              // Try to extract template content
              messagePreview = item?.lastMessage?.message?.body?.text?.body || 'Template message';
              break;
              
            case 'text':
            default:
              // For text messages, handle different structures
              if (item?.lastMessage?.message?.body) {
                messagePreview = typeof item.lastMessage.message.body === 'string' 
                  ? item.lastMessage.message.body 
                  : (item.lastMessage.message.body?.text?.body || 'Message');
              } else {
                messagePreview = 'Message';
              }
              break;
          }
          
          // Transform each conversation item with proper typing
          groupedData[date].push({
            id: item?._id,
            _id: item?._id,
            name: item?.lead?.name,
            mobile: item?.lead?.phone,
            lastMessageAt: item?.lastMessageAt,
            email: item?.lead?.email,
            userIcon: `${localStorage.getItem('bucketUrl')}${item?.profile?.filePath}`,
            msg: messagePreview, // Use our processed message preview
            lead: item?.lead?._id,
            assignedTo: item?.assignedTo || [],
            timeStamp: moment(item?.lastMessage?.createdAt).format('LT'),
            msgStatus: item?.lastMessage?.status,
            convoId: item?.conversationId,
            unReadCount: item?.unReadCount,
            label: item?.lead?.label || [],
            status: item?.lead?.status || [],
            // Store the full lastMessage for reference
            lastMessage: item?.lastMessage
          });
        });
        
        // Convert to array of groups with proper typing
        const groupedArr: ConversationGroup[] = Object.entries(groupedData).map(
          ([title, data]) => ({ title, data })
        );
        
        // If refreshing or page 1, replace data, otherwise append
        if (refresh || page === 1) {
          state.groups = groupedArr;
        } else {
          // Append new groups or merge with existing groups
          const existingTitles = new Set(state.groups.map(g => g.title));
          
          groupedArr.forEach((newGroup) => {
            if (existingTitles.has(newGroup.title)) {
              // Merge with existing group
              const existingGroup = state.groups.find(g => g.title === newGroup.title);
              if (existingGroup) {
                // Add unique conversations by id
                const existingIds = new Set(existingGroup.data.map(c => c.id));
                newGroup.data.forEach((conversation) => {
                  if (!existingIds.has(conversation.id)) {
                    existingGroup.data.push(conversation);
                  }
                });
              }
            } else {
              // Add new group
              state.groups.push(newGroup);
            }
          });
        }
        
        // Sort groups by date (newest first)
        state.groups.sort((a, b) => {
          return new Date(b.title).getTime() - new Date(a.title).getTime();
        });
        
        // Select first conversation if none selected and we have data
        if (!state.selectedConversation && state.groups.length > 0 && state.groups[0].data.length > 0) {
          state.selectedConversation = state.groups[0].data[0];
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      
      // Handle assignConversationToUser
      .addCase(assignConversationToUser.fulfilled, (state, action) => {
        const { conversationId, assignedUser } = action.payload;
        
        // Update in grouped data
        state.groups = state.groups.map(group => {
          const updatedData = group.data.map(conversation => {
            if (conversation._id === conversationId) {
              return {
                ...conversation,
                assignedTo: [...conversation.assignedTo, assignedUser]
              };
            }
            return conversation;
          });
          
          return {
            ...group,
            data: updatedData
          };
        });
        
        // Also update in selected conversation if it matches
        if (state.selectedConversation?._id === conversationId) {
          state.selectedConversation = {
            ...state.selectedConversation,
            assignedTo: [...state.selectedConversation.assignedTo, assignedUser]
          };
        }
      })
      
      // Handle deleteConversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const conversationId = action.payload;
        
        // Remove from groups
        state.groups = state.groups.map(group => ({
          ...group,
          data: group.data.filter(conversation => conversation._id !== conversationId)
        })).filter(group => group.data.length > 0); // Remove empty groups
        
        // Clear selected conversation if it's the deleted one
        if (state.selectedConversation?._id === conversationId) {
          state.selectedConversation = null;
          
          // Select first conversation if available
          if (state.groups.length > 0 && state.groups[0].data.length > 0) {
            state.selectedConversation = state.groups[0].data[0];
          }
        }
      })
      
      // Handle startNewChat
      .addCase(startNewChat.fulfilled, (state) => {
        // We don't update state here - we'll fetch conversations to get the new chat
      });
  }
});

// Export actions
export const { 
  setSelectedConversation, 
  clearSelectedConversation,
  setFilter,
  toggleFilter,
  clearFilters,
  setCurrentPage,
   resetPagination,
  updateLastMessage
} = conversationsSlice.actions;

// Export selectors
export const selectAllConversations = (state: RootState) => state.whatsapp.conversations.groups;
export const selectSelectedConversation = (state: RootState) => state.whatsapp.conversations.selectedConversation;
export const selectConversationsStatus = (state: RootState) => state.whatsapp.conversations.status;
export const selectTotalConversations = (state: RootState) => state.whatsapp.conversations.totalConversations;
export const selectCurrentPage = (state: RootState) => state.whatsapp.conversations.currentPage;
export const selectFilters = (state: RootState) => state.whatsapp.conversations.filters;

// Export reducer
export default conversationsSlice.reducer;